/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { RoleEligibilitySchedules } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { AuthorizationManagementClient } from "../authorizationManagementClient";
import {
  RoleEligibilitySchedule,
  RoleEligibilitySchedulesListForScopeNextOptionalParams,
  RoleEligibilitySchedulesListForScopeOptionalParams,
  RoleEligibilitySchedulesListForScopeResponse,
  RoleEligibilitySchedulesGetOptionalParams,
  RoleEligibilitySchedulesGetResponse,
  RoleEligibilitySchedulesListForScopeNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing RoleEligibilitySchedules operations. */
export class RoleEligibilitySchedulesImpl implements RoleEligibilitySchedules {
  private readonly client: AuthorizationManagementClient;

  /**
   * Initialize a new instance of the class RoleEligibilitySchedules class.
   * @param client Reference to the service client
   */
  constructor(client: AuthorizationManagementClient) {
    this.client = client;
  }

  /**
   * Gets role eligibility schedules for a resource scope.
   * @param scope The scope of the role eligibility schedules.
   * @param options The options parameters.
   */
  public listForScope(
    scope: string,
    options?: RoleEligibilitySchedulesListForScopeOptionalParams
  ): PagedAsyncIterableIterator<RoleEligibilitySchedule> {
    const iter = this.listForScopePagingAll(scope, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listForScopePagingPage(scope, options, settings);
      }
    };
  }

  private async *listForScopePagingPage(
    scope: string,
    options?: RoleEligibilitySchedulesListForScopeOptionalParams,
    settings?: PageSettings
  ): AsyncIterableIterator<RoleEligibilitySchedule[]> {
    let result: RoleEligibilitySchedulesListForScopeResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listForScope(scope, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listForScopeNext(scope, continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listForScopePagingAll(
    scope: string,
    options?: RoleEligibilitySchedulesListForScopeOptionalParams
  ): AsyncIterableIterator<RoleEligibilitySchedule> {
    for await (const page of this.listForScopePagingPage(scope, options)) {
      yield* page;
    }
  }

  /**
   * Get the specified role eligibility schedule for a resource scope
   * @param scope The scope of the role eligibility schedule.
   * @param roleEligibilityScheduleName The name (guid) of the role eligibility schedule to get.
   * @param options The options parameters.
   */
  get(
    scope: string,
    roleEligibilityScheduleName: string,
    options?: RoleEligibilitySchedulesGetOptionalParams
  ): Promise<RoleEligibilitySchedulesGetResponse> {
    return this.client.sendOperationRequest(
      { scope, roleEligibilityScheduleName, options },
      getOperationSpec
    );
  }

  /**
   * Gets role eligibility schedules for a resource scope.
   * @param scope The scope of the role eligibility schedules.
   * @param options The options parameters.
   */
  private _listForScope(
    scope: string,
    options?: RoleEligibilitySchedulesListForScopeOptionalParams
  ): Promise<RoleEligibilitySchedulesListForScopeResponse> {
    return this.client.sendOperationRequest(
      { scope, options },
      listForScopeOperationSpec
    );
  }

  /**
   * ListForScopeNext
   * @param scope The scope of the role eligibility schedules.
   * @param nextLink The nextLink from the previous successful call to the ListForScope method.
   * @param options The options parameters.
   */
  private _listForScopeNext(
    scope: string,
    nextLink: string,
    options?: RoleEligibilitySchedulesListForScopeNextOptionalParams
  ): Promise<RoleEligibilitySchedulesListForScopeNextResponse> {
    return this.client.sendOperationRequest(
      { scope, nextLink, options },
      listForScopeNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/{scope}/providers/Microsoft.Authorization/roleEligibilitySchedules/{roleEligibilityScheduleName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RoleEligibilitySchedule
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion2],
  urlParameters: [
    Parameters.$host,
    Parameters.scope,
    Parameters.roleEligibilityScheduleName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listForScopeOperationSpec: coreClient.OperationSpec = {
  path: "/{scope}/providers/Microsoft.Authorization/roleEligibilitySchedules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RoleEligibilityScheduleListResult
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.filter, Parameters.apiVersion2],
  urlParameters: [Parameters.$host, Parameters.scope],
  headerParameters: [Parameters.accept],
  serializer
};
const listForScopeNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RoleEligibilityScheduleListResult
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  urlParameters: [Parameters.$host, Parameters.nextLink, Parameters.scope],
  headerParameters: [Parameters.accept],
  serializer
};
